/**
 * 手机预览组件
 * 
 * v11.2.2 新增：模拟 App 的角色展示效果
 * 用于发布项目时实时预览角色在 App 中的显示样式
 */

import { User, Mic, Star } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'

interface PhonePreviewProps {
  /** 角色形象图片 URL */
  avatarUrl?: string
  /** 角色名称 */
  displayName?: string
  /** 角色定位/副标题 */
  subtitle?: string
  /** 角色介绍 */
  description?: string
  /** 标签列表 */
  tags?: string[]
  /** 是否推荐 */
  isRecommended?: boolean
  /** 自定义类名 */
  className?: string
}

export default function PhonePreview({
  avatarUrl,
  displayName = '角色名称',
  subtitle,
  description,
  tags = [],
  isRecommended = false,
  className,
}: PhonePreviewProps) {
  return (
    <div className={cn('flex flex-col items-center', className)}>
      {/* 手机外框 */}
      <div className="relative w-[240px] h-[520px] bg-black rounded-[32px] p-2 shadow-2xl">
        {/* 手机屏幕 */}
        <div className="w-full h-full bg-gradient-to-b from-slate-900 to-slate-800 rounded-[24px] overflow-hidden relative">
          {/* 状态栏 */}
          <div className="absolute top-0 left-0 right-0 h-6 flex items-center justify-between px-4 text-white/80 text-[10px] z-20">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-4 h-2 border border-white/80 rounded-sm">
                <div className="w-2/3 h-full bg-white/80 rounded-sm" />
              </div>
            </div>
          </div>

          {/* 角色形象背景 */}
          <div className="absolute inset-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-b from-slate-700 to-slate-900 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-slate-600 flex items-center justify-center">
                  <User className="w-12 h-12 text-slate-400" />
                </div>
              </div>
            )}
            {/* 渐变遮罩 */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          </div>

          {/* 顶部标签 */}
          <div className="absolute top-8 left-3 right-3 flex items-center justify-between z-10">
            {isRecommended && (
              <Badge className="bg-amber-500/90 text-white border-0 gap-1 text-[10px] px-2 py-0.5">
                <Star className="w-3 h-3 fill-current" />
                推荐
              </Badge>
            )}
            <div className="flex-1" />
            <Badge variant="outline" className="text-white/80 border-white/30 text-[10px] px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1" />
              在线
            </Badge>
          </div>

          {/* 底部内容区 */}
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
            {/* 标签 */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {tags.slice(0, 4).map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-white/10 text-white/90 border-0 text-[10px] px-2 py-0.5"
                  >
                    {tag}
                  </Badge>
                ))}
                {tags.length > 4 && (
                  <Badge
                    variant="secondary"
                    className="bg-white/10 text-white/60 border-0 text-[10px] px-2 py-0.5"
                  >
                    +{tags.length - 4}
                  </Badge>
                )}
              </div>
            )}

            {/* 角色名称 */}
            <h3 className="text-white text-lg font-bold leading-tight">
              {displayName || '角色名称'}
            </h3>

            {/* 副标题 */}
            {subtitle && (
              <p className="text-white/70 text-xs mt-0.5 line-clamp-1">
                {subtitle}
              </p>
            )}

            {/* 角色介绍 */}
            {description && (
              <p className="text-white/60 text-[11px] mt-2 line-clamp-2 leading-relaxed">
                "{description}"
              </p>
            )}

            {/* 语音输入提示（模拟） */}
            <div className="mt-4 flex items-center justify-center">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Mic className="w-4 h-4 text-white/80" />
                <span className="text-white/60 text-xs">想对 TA 说点什么...</span>
              </div>
            </div>
          </div>

          {/* 底部导航栏（模拟） */}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-black/50 backdrop-blur-sm flex items-center justify-around border-t border-white/10">
            <NavItem label="人格" active />
            <NavItem label="记忆" />
            <NavItem label="发现" />
            <NavItem label="设置" />
          </div>
        </div>

        {/* 刘海 */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-30" />
      </div>

      {/* 预览标签 */}
      <div className="mt-3 text-xs text-muted-foreground">
        📱 App 预览效果
      </div>
    </div>
  )
}

// 底部导航项
function NavItem({ label, active = false }: { label: string; active?: boolean }) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-0.5 px-3 py-1',
        active ? 'text-white' : 'text-white/40'
      )}
    >
      <div className={cn(
        'w-5 h-5 rounded-full',
        active ? 'bg-white/20' : 'bg-white/5'
      )} />
      <span className="text-[10px]">{label}</span>
    </div>
  )
}
